

/**
 * Comparison Bar Component
 * Floating bar showing selected items with compare action
 */

import { useState, useMemo } from 'react';
import { GitCompare, X, ChevronUp, ChevronDown } from 'lucide-react';
import { useComparison } from '@/hooks/use-comparison';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ComparisonBarProps {
  position?: 'bottom' | 'top';
  showLabels?: boolean;
  maxVisibleItems?: number;
  className?: string;
}

export function ComparisonBar({
  position = 'bottom',
  showLabels = true,
  maxVisibleItems = 3,
  className,
}: ComparisonBarProps) {
  const { items, count, removeItem, setModalOpen, clearItems } = useComparison();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const visibleItems = useMemo(() => {
    return items.slice(0, maxVisibleItems);
  }, [items, maxVisibleItems]);

  const remainingCount = useMemo(() => {
    return Math.max(0, items.length - maxVisibleItems);
  }, [items.length, maxVisibleItems]);

  if (items.length < 2) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          'fixed z-50 transition-all duration-300 ease-in-out',
          position === 'bottom' ? 'bottom-4 left-1/2 -translate-x-1/2' : 'top-4 left-1/2 -translate-x-1/2',
          isCollapsed ? 'w-auto' : 'w-full max-w-4xl',
          className
        )}
      >
        <div className={cn(
          'bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80',
          'border rounded-full shadow-lg',
          'flex items-center gap-2 px-4 py-2'
        )}>
          {/* Collapse Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="h-8 w-8 shrink-0"
            aria-label={isCollapsed ? 'Expand comparison bar' : 'Collapse comparison bar'}
          >
            {isCollapsed ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>

          {!isCollapsed && (
            <>
              {/* Item Avatars */}
              <div className="flex items-center gap-1">
                {visibleItems.map((item) => (
                  <Tooltip key={item.id}>
                    <TooltipTrigger asChild>
                      <div className="relative group">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                          {item.data.logo ? (
                            <img
                              src={item.data.logo}
                              alt={item.data.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="text-sm font-medium text-primary">
                              {item.data.name.charAt(0)}
                            </span>
                          )}
                        </div>
                        {/* Remove button on hover */}
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute -top-1 -right-1 h-5 w-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeItem(item.id);
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-medium">{item.data.name}</p>
                      <p className="text-xs text-muted-foreground">{item.data.location.city}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}

                {remainingCount > 0 && (
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                    <span className="text-sm font-medium text-muted-foreground">
                      +{remainingCount}
                    </span>
                  </div>
                )}
              </div>

              {/* Count */}
              {showLabels && (
                <div className="text-sm font-medium px-2">
                  <span className="text-primary">{count}</span>
                  <span className="text-muted-foreground">/{4} selected</span>
                </div>
              )}

              {/* Spacer */}
              <div className="flex-1" />

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearItems}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      Clear
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Clear all items</p>
                  </TooltipContent>
                </Tooltip>

                <Button
                  size="sm"
                  onClick={() => setModalOpen(true)}
                  className="gap-2"
                >
                  <GitCompare className="h-4 w-4" />
                  Compare Now
                </Button>
              </div>
            </>
          )}

          {isCollapsed && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setModalOpen(true)}
              className="gap-2"
            >
              <GitCompare className="h-4 w-4" />
              <span>{count} selected</span>
            </Button>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
